import { Eye, EyeSlash, StackSimple, User, UsersThree, X } from '@phosphor-icons/react';

// F-5 de ARQ-007: la pantalla «Filtros» de Procore, medida en la segunda
// grabación. Son DOS preguntas distintas y el panel las separa porque se
// responden por motivos distintos:
//
//   QUÉ SE VE   — por tipo de anotación (marcas, medidas, fotos, chinches,
//                 vínculos). Sirve para despejar: un plano con treinta medidas
//                 encima no se puede leer.
//   DE QUIÉN    — publicada (la ve todo el mundo) contra personal (la ve solo
//                 quien la escribió). Sirve para separar la nota de trabajo del
//                 acuerdo con el resto del equipo.
//
// El flag `privada` ya existía punta a punta desde F-1 (columna, entidad, DTO,
// filtro del servidor, interfaz y las dos mutaciones) pero NO tenía ninguna UI:
// no se podía ni ver ni fijar. F-5 no agrega el dato, lo saca a la superficie.
//
// El filtro es del VISOR, no del servidor: se pinta sobre lo que ya llegó, así
// que apagar y prender capas no pide una sola request.

export interface Capas {
  /** Dibujos: figuras, líneas, trazo libre, notas de texto. */
  marcas: boolean;
  /** Distancias y áreas. Subconjunto de las marcas, no una tabla aparte. */
  medidas: boolean;
  fotos: boolean;
  chinches: boolean;
  vinculos: boolean;
  /** Las que ve todo el equipo. */
  publicadas: boolean;
  /** Las propias, que nadie más recibe. */
  personales: boolean;
}

export const CAPAS_TODAS: Capas = {
  marcas: true, medidas: true, fotos: true, chinches: true, vinculos: true,
  publicadas: true, personales: true,
};

/** ¿Hay algo apagado? Lo usa el aviso del visor, no el panel. */
export const hayCapasOcultas = (c: Capas) => Object.values(c).some(v => !v);

export type Conteos = Record<keyof Capas, number>;

interface Props {
  capas: Capas;
  conteos: Conteos;
  onCambiar: (clave: keyof Capas, valor: boolean) => void;
  onVerTodo: () => void;
  onCerrar: () => void;
}

export function PanelCapas({ capas, conteos, onCambiar, onVerTodo, onCerrar }: Props) {
  return (
    // `bg-white/95`, no `/97`: Tailwind solo genera los pasos de su escala de
    // opacidad (…85, 90, 95, 100) y `/97` no produce NINGUNA regla, así que el
    // panel quedaba transparente y el texto se leía contra el plano. Medido el
    // 20-oct-2026 con getComputedStyle: `/97` daba rgba(0,0,0,0).
    <div className="w-60 rounded-lg border border-brand-gray-200 bg-white/95 p-2 shadow-lg">
      <div className="mb-1.5 flex items-center gap-1.5 px-1">
        <StackSimple size={14} weight="bold" className="text-brand-gray-500" />
        <p className="flex-1 text-xs font-semibold text-brand-gray-500">Capas y filtros</p>
        {hayCapasOcultas(capas) && (
          <button type="button" onClick={onVerTodo}
            className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-brand-gray-500 hover:bg-brand-gray-100">
            Ver todo
          </button>
        )}
        <button type="button" onClick={onCerrar} aria-label="Cerrar las capas"
          className="rounded p-0.5 text-brand-gray-400 hover:bg-brand-gray-100">
          <X size={13} weight="bold" />
        </button>
      </div>

      <Titulo>Qué se ve</Titulo>
      <Fila clave="marcas" nombre="Marcas" {...{ capas, conteos, onCambiar }} />
      <Fila clave="medidas" nombre="Medidas" {...{ capas, conteos, onCambiar }} />
      <Fila clave="fotos" nombre="Fotos" {...{ capas, conteos, onCambiar }} />
      <Fila clave="chinches" nombre="Chinches de tarea" {...{ capas, conteos, onCambiar }} />
      <Fila clave="vinculos" nombre="Vínculos" {...{ capas, conteos, onCambiar }} />

      <div className="my-1.5 h-px bg-brand-gray-200" />

      <Titulo>De quién</Titulo>
      <Fila clave="publicadas" nombre="Publicadas" icono={<UsersThree size={12} weight="bold" />}
        {...{ capas, conteos, onCambiar }} />
      <Fila clave="personales" nombre="Personales (mías)" icono={<User size={12} weight="bold" />}
        {...{ capas, conteos, onCambiar }} />
      {/* Sin esto, apagar «Publicadas» y ver que las fotos siguen ahí se lee
          como que el filtro no funciona. Solo las marcas tienen dueño. */}
      <p className="mt-1 px-1 text-[10px] leading-snug text-brand-gray-400">
        Solo aplica a marcas y medidas. Las fotos, los chinches y los vínculos
        son siempre del equipo.
      </p>
    </div>
  );
}

function Titulo({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-0.5 px-1 text-[10px] font-bold uppercase tracking-wide text-brand-gray-400">
      {children}
    </p>
  );
}

function Fila({ clave, nombre, icono, capas, conteos, onCambiar }: {
  clave: keyof Capas;
  nombre: string;
  icono?: React.ReactNode;
  capas: Capas;
  conteos: Conteos;
  onCambiar: (clave: keyof Capas, valor: boolean) => void;
}) {
  const visible = capas[clave];
  const cuantos = conteos[clave];
  return (
    <button
      type="button"
      // Expuesto en el DOM: el `title` del botón cambia con el estado, así que
      // sin esto la prueba de UI no tiene forma estable de agarrar la fila.
      data-capa={clave}
      aria-pressed={visible}
      onClick={() => onCambiar(clave, !visible)}
      title={visible ? `Ocultar ${nombre.toLowerCase()}` : `Mostrar ${nombre.toLowerCase()}`}
      className={`flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-xs hover:bg-brand-gray-100 ${
        // Una capa vacía se atenúa pero sigue siendo clickeable: apagarla de
        // antemano es legítimo, y un caso especial para «0» no gana nada.
        cuantos === 0 ? 'opacity-45' : ''
      }`}
    >
      {visible
        ? <Eye size={13} weight="bold" className="shrink-0 text-brand-gray-500" />
        : <EyeSlash size={13} weight="bold" className="shrink-0 text-brand-gray-300" />}
      {icono && <span className="shrink-0 text-brand-gray-400">{icono}</span>}
      <span className={`flex-1 truncate ${visible ? 'font-semibold text-brand-gray-500' : 'text-brand-gray-400'}`}>
        {nombre}
      </span>
      <span className="shrink-0 font-mono text-[11px] text-brand-gray-400">{cuantos}</span>
    </button>
  );
}
